import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["$cssStyles", "$hasChildren", "$inline", "$innerPadding", "$isDisplayOptimized", "description", "descriptionLayout", "disabled", "readOnly"],
  _excluded2 = ["$disabled", "$readOnly"],
  _excluded3 = ["$readOnly", "$indeterminate", "$disabled", "theme"],
  _excluded4 = ["data-vr-focus", "aria-label", "aria-labelledby", "autoFocus", "checked", "defaultChecked", "children", "color", "description", "disabled", "id", "indeterminate", "name", "onChange", "readOnly", "size", "tabIndex", "value", "title", "alignment", "descriptionLayout", "inline", "innerPadding"];
import { forwardRef, useContext, useState } from 'react';
import styled from 'foundations-theming/utils';
import { FieldsetContext } from '../../context/FieldsetContext';
import { Checkmark } from './Checkmark';
import { getTabIndex } from '../../utils/TabIndex';
import { ToggleInputWrapper } from '../shared-components/ToggleInputWrapper';
import useUniqueId from 'react-utils/hooks/useUniqueId';
import devLogger from 'react-utils/devLogger';
import { filterProps } from '../../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { useIsDisplayOptimized } from '../../context';
import { SIZE } from '../../../constants/nonTokenisedValues';
import { getScreenReaderOnlyStyles } from '../../utils/styles/styles';
import { getFocusPseudoClass, getFocusStyleObject } from '../../utils/focus/focus.style';
import { TRANSITION_ALL_STYLE } from '../../../constants/transition-all';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const CheckboxWrapper = styled(ToggleInputWrapper)(_ref => {
  let {
      $cssStyles,
      $hasChildren,
      $inline,
      $innerPadding,
      $isDisplayOptimized,
      description,
      descriptionLayout,
      disabled,
      readOnly
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const theme = rest.theme;
  return Object.assign({
    userSelect: 'none'
  }, !readOnly && !disabled && {
    '&:hover [data-indicator="true"]': {
      backgroundColor: `${theme.color['fill-field-hover']} !important`
    }
  }, description && descriptionLayout === 'vertical' && {
    flexDirection: 'column',
    flexWrap: 'wrap',
    marginTop: '6px'
  }, $hasChildren && {
    height: SIZE.CHECKBOX_WRAPPER_WITH_CHILDREN,
    width: SIZE.CHECKBOX_WRAPPER_WITH_CHILDREN
  }, ($hasChildren && !$inline || $innerPadding === 'none') && {
    display: 'block',
    minHeight: '0'
  }, $isDisplayOptimized && {
    minHeight: '40px'
  }, $cssStyles);
});
const CheckboxIndicator = styled.span.withConfig({
  displayName: "Checkbox__CheckboxIndicator",
  componentId: "b5ctid-0"
})(_ref2 => {
  let {
      $disabled,
      $readOnly
    } = _ref2,
    rest = _objectWithoutPropertiesLoose(_ref2, _excluded2);
  const theme = rest.theme;
  let borderColor = '';
  if ($readOnly || $disabled) {
    borderColor = theme.color['border-core-disabled'];
  } else {
    borderColor = theme.color['border-core-default'];
  }
  return Object.assign({
    flexShrink: 0,
    backgroundColor: theme.color['fill-field-default-alt'],
    color: theme.color['border-core-default'],
    height: '1.25em',
    left: '0.0625em',
    position: 'relative',
    width: '1.25em',
    border: `${theme.borderWidth['100']} solid ${borderColor}`,
    borderRadius: theme.borderRadius['200'],
    transition: TRANSITION_ALL_STYLE
  }, !$readOnly && !$disabled && {
    '&:hover': {
      backgroundColor: `${theme.color['fill-field-hover']} !important`
    }
  });
});
const CheckboxIcon = styled.span.withConfig({
  displayName: "Checkbox__CheckboxIcon",
  componentId: "b5ctid-1"
})(["align-items:center;display:flex;justify-content:center;bottom:0;left:0;opacity:0;position:absolute;right:0;top:0;> svg{fill:currentColor;width:94%;}"]);
const CheckboxInput = styled.input.withConfig({
  displayName: "Checkbox__CheckboxInput",
  componentId: "b5ctid-2"
})(_ref3 => {
  let {
      $readOnly,
      $indeterminate,
      $disabled,
      theme
    } = _ref3,
    rest = _objectWithoutPropertiesLoose(_ref3, _excluded3);
  const $color = rest.$color;
  const isBorderless = $readOnly || $disabled;
  const computedIconColor = $disabled ? theme.color['icon-core-disabled'] : $readOnly ? theme.color['icon-core-subtle'] : theme.color['icon-interactive-default'];
  const computedColor = $color && $color in theme.color ? theme.color[$color] : computedIconColor;
  return Object.assign({}, getScreenReaderOnlyStyles(theme), {
    // hubspot-enable-focus-styles class styles
    '&:focus-visible': {
      [`& + ${CheckboxIndicator}`]: {
        // boxShadow:
        // 'rgba(0, 208, 228, 0.3) 0px 0px 4px 1px, rgb(0, 208, 228) 0px 0px 0px 1px !important',
      }
    },
    '&:checked': {
      [`& + ${CheckboxIndicator}`]: {
        borderColor: !isBorderless ? 'currentColor' : undefined,
        color: $color && $color in theme.color ? theme.color[$color] : theme.color['border-interactive-pressed']
      },
      [`+ ${CheckboxIndicator} ${CheckboxIcon}:first-child`]: {
        fill: theme.color['icon-interactive-default'],
        color: computedColor,
        opacity: '1 !important'
      }
    }
  }, $indeterminate && {
    '&': {
      [`+ ${CheckboxIndicator}`]: {
        borderColor: !isBorderless && 'currentColor',
        color: $color && $color in theme.color ? theme.color[$color] : theme.color['border-interactive-pressed']
      }
    },
    '&:not(:checked)': {
      [`+ ${CheckboxIndicator} ${CheckboxIcon}:first-child`]: {
        opacity: 0,
        color: computedIconColor
      },
      [`+ ${CheckboxIndicator} ${CheckboxIcon}:last-child`]: {
        opacity: 1,
        color: computedColor,
        fill: computedColor,
        '> svg': {
          width: '100%'
        }
      }
    },
    '&:checked': {
      [`+ ${CheckboxIndicator} ${CheckboxIcon}:first-child`]: {
        opacity: 1,
        color: computedIconColor
      },
      [`+ ${CheckboxIndicator} ${CheckboxIcon}:last-child`]: {
        opacity: 0,
        color: computedIconColor
      }
    },
    '&:disabled': {
      [`+ ${CheckboxIndicator} ${CheckboxIcon}:last-child`]: {
        opacity: 1,
        color: theme.color['icon-core-disabled']
      }
    }
  }, {
    '&:focus:not(:checked)': {
      [`+ ${CheckboxIndicator}`]: {
        borderColor: !isBorderless && theme.color['border-core-default']
      }
    },
    [getFocusPseudoClass()]: {
      [`&:not([readonly]) + ${CheckboxIndicator}`]: Object.assign({}, getFocusStyleObject({
        theme,
        unwrapped: true
      })),
      [`&[readonly] + ${CheckboxIndicator}`]: {
        outline: 'auto'
      }
    }
  });
});
const CheckboxText = styled.span.withConfig({
  displayName: "Checkbox__CheckboxText",
  componentId: "b5ctid-3"
})(({
  $small,
  theme
}) => {
  return Object.assign({}, $small && {
    fontSize: theme.typography['body-100'].fontSize
  });
});
export const Checkbox = /*#__PURE__*/forwardRef((_ref4, ref) => {
  let {
      'data-vr-focus': dataVrFocus,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      autoFocus = false,
      checked: checkedProp,
      defaultChecked = false,
      children,
      color,
      description,
      disabled,
      id,
      indeterminate = false,
      name,
      onChange,
      readOnly = false,
      size,
      tabIndex,
      value,
      title,
      alignment = 'start',
      descriptionLayout = 'vertical',
      inline = false,
      innerPadding = 'default'
    } = _ref4,
    rest = _objectWithoutPropertiesLoose(_ref4, _excluded4);
  const {
    size: fieldSize,
    disabled: fieldDisabled
  } = useContext(FieldsetContext);
  const isDisplayOptimized = useIsDisplayOptimized();
  const descriptionId = useUniqueId('checkbox-');
  const childWrapperId = useUniqueId('checkbox-content-');
  const computedDisabled = disabled || fieldDisabled;
  const computedSize = size || fieldSize;
  const isControlled = checkedProp !== undefined;
  const [internalChecked, setInternalChecked] = useState(defaultChecked);
  const checkedValue = isControlled ? checkedProp : internalChecked;
  const handleChange = event => {
    if (!isControlled) {
      setInternalChecked(event.target.checked);
    }
    if (onChange) onChange(event);
  };
  const ariaLabelledByIds = `${ariaLabelledBy !== null && ariaLabelledBy !== void 0 ? ariaLabelledBy : ''} ${children ? childWrapperId : ''}`.trim();
  if (process.env.NODE_ENV !== 'production') {
    if (children == null && !ariaLabel && !ariaLabelledBy) {
      devLogger.warn({
        message: "Checkbox: `aria-label` or `aria-labelledby` is required if you don't define `children`.",
        key: 'Checkbox: no label'
      });
    }
    if (ariaLabel && (children || ariaLabelledBy)) {
      devLogger.warn({
        message: 'Checkbox: `aria-label` should not be used with `children` or `aria-labelledby`.',
        key: 'Checkbox: aria-label hides children',
        url: 'https://git.hubteam.com/HubSpot/Components/pull/8473/files#r1011220'
      });
    }
  }
  const indeterminateAttributes = indeterminate && !checkedValue ? {
    'aria-checked': 'mixed'
  } : {};
  const {
    domProps,
    tokenProps,
    callbackProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  return /*#__PURE__*/_jsx(CheckboxWrapper, Object.assign({
    checked: checkedValue,
    childWrapperId: childWrapperId,
    descriptionId: descriptionId,
    description: description,
    descriptionLayout: descriptionLayout,
    $description: description,
    $descriptionLayout: descriptionLayout,
    disabled: computedDisabled,
    $hasChildren: children == null,
    inline: inline,
    $inline: inline,
    $innerPadding: innerPadding,
    $isDisplayOptimized: isDisplayOptimized,
    $cssStyles: cssStyles,
    alignment: alignment,
    input: /*#__PURE__*/_jsx(CheckboxInput, Object.assign({
      "data-vr-focus": dataVrFocus,
      "aria-describedby": description ? descriptionId : undefined,
      "aria-label": ariaLabel,
      "aria-labelledby": ariaLabelledByIds || undefined,
      autoFocus: autoFocus,
      checked: checkedValue,
      disabled: computedDisabled,
      id: id,
      name: name,
      onChange: evt => {
        if (disabled || readOnly) {
          evt.preventDefault();
          return;
        }
        handleChange(evt);
      },
      $color: color,
      $readOnly: readOnly,
      readOnly: readOnly // to pass through to DOM
      ,
      tabIndex: getTabIndex(readOnly || computedDisabled, tabIndex),
      type: "checkbox",
      value: value,
      $disabled: computedDisabled,
      $indeterminate: indeterminate
    }, indeterminateAttributes)),
    indicator: /*#__PURE__*/_jsxs(CheckboxIndicator, {
      $disabled: disabled,
      $readOnly: readOnly,
      "data-indicator": true,
      children: [/*#__PURE__*/_jsx(CheckboxIcon, {
        children: /*#__PURE__*/_jsx(Checkmark, {})
      }), /*#__PURE__*/_jsx(CheckboxIcon, {
        children: /*#__PURE__*/_jsx("svg", {
          role: "presentation",
          viewBox: "0 0 14 14",
          xmlns: "http://www.w3.org/2000/svg",
          children: /*#__PURE__*/_jsx("path", {
            d: "M 2 6 L12 6 L12 9 L2 9 L2 6 z"
          })
        })
      })]
    }),
    onChange: handleChange,
    readOnly: readOnly,
    ref: ref,
    size: computedSize,
    "data-checkbox": true,
    title: title
  }, domProps, callbackProps, {
    children: children && /*#__PURE__*/_jsx(CheckboxText, {
      $small: computedSize === 'sm' || isDisplayOptimized,
      children: children
    })
  }));
});
Checkbox.displayName = 'Checkbox';